import type { RouterOutputs } from "@repo/api";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import type { PartialWithRequired } from "~/types";
import CashLocationDialog from "~/components/cash-location/cash-location-dialog";
import MutationDialog from "~/components/cash-location/mutation-dialog";
import { CashLocationContext } from "~/hooks/use-cash-location";
import { api } from "~/lib/api";
import { useLoadingStore } from "~/stores/use-loading-store";

export type CashLocation = RouterOutputs["cashLocation"]["getAll"][number];
export type CashLocationMutationCategory =
  RouterOutputs["cashLocation"]["getMutationCategories"][number];

export type CashLocationMutation = RouterOutputs["report"]["getMutations"][0];

export type PartialCashLocationMutation = PartialWithRequired<
  CashLocationMutation,
  | "value"
  | "type"
  | "vatRate"
  | "comment"
  | "createdAt"
  | "cashLocation"
  | "category"
>;

interface CashLocationProviderProps {
  children: ReactNode;
}

export const CashLocationProvider = ({
  children,
}: CashLocationProviderProps) => {
  const { data: cashLocations, isFetching: isCashLocationsLoading } =
    api.cashLocation.getAll.useQuery(undefined, {
      initialData: [],
    });

  const { data: cashLocationMutationCategories } =
    api.cashLocation.getMutationCategories.useQuery(undefined, {
      initialData: [],
    });

  const { setLoadingStage } = useLoadingStore(
    useShallow((state) => ({
      setLoadingStage: state.setCurrentLoadingStage,
    })),
  );

  useEffect(() => {
    if (isCashLocationsLoading) {
      setLoadingStage("buttons");
    } else {
      setLoadingStage(undefined);
    }
  }, [isCashLocationsLoading, setLoadingStage]);

  const context = {
    cashLocations,
    cashLocationMutationCategories,
  };

  return (
    <CashLocationContext.Provider value={context}>
      {children}
      <CashLocationDialog />
      <MutationDialog />
    </CashLocationContext.Provider>
  );
};
